import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./LoginPage.module.css";

export function LoginPage() {
  const { t, tOr, locale, setLocale, locales } = useLocale();
  const login = useAuth((s) => s.login);
  const loadTenant = useAuth((s) => s.loadTenant);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errCode, setErrCode] = useState<string | null>(null);
  const [confirmedNotice, setConfirmedNotice] = useState(false);

  // The /confirm-email page redirects back here with ?confirmed=1 on
  // success; surface a one-time success banner so the user knows their
  // email is verified before they sign in.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("confirmed") === "1") setConfirmedNotice(true);
  }, [location.search]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrCode(null);
    try {
      const target = await login(email.trim(), password);
      await loadTenant();
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setErrCode(err.code);
      else setErrCode("unknown");
      setBusy(false);
    }
  }

  return (
    <main className={styles.shell}>
      <div className={styles.langToggle}>
        {locales.map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={l === locale}
            onClick={() => setLocale(l)}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <form className={styles.card} onSubmit={onSubmit}>
        <h1 className={styles.title}>{t("login.title")}</h1>
        <p className={styles.subtitle}>{t("login.subtitle")}</p>
        {confirmedNotice && (
          <p className={styles.success}>{t("login.confirmed_notice")}</p>
        )}
        <label className={styles.label}>
          <span>{t("login.email")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            spellCheck={false}
            required
          />
        </label>
        <label className={styles.label}>
          <span>{t("login.password")}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            minLength={8}
          />
        </label>
        {errCode && (
          <p className={styles.error}>
            {tOr(`login.error.${errCode}`, "login.error.unknown")}
          </p>
        )}
        <button type="submit" className={styles.submit} disabled={busy}>
          {busy ? t("common.loading") : t("login.submit")}
        </button>
        <div className={styles.links}>
          <Link to="/signup">{t("login.link.signup")}</Link>
          <Link to="/forgot-password">{t("login.link.forgot")}</Link>
        </div>
      </form>
    </main>
  );
}
